import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart2, Music, Clock, Zap, Key, TrendingUp, 
  Loader2, Box, Users, Award
} from 'lucide-react';
import './StatisticsDashboard.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StatisticsDashboard = ({ onClose }) => {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const getAuthToken = useCallback(() => localStorage.getItem('beatseek_token'), []);

  const fetchStats = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/api/crates/statistics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to load statistics');

      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [getAuthToken]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (isLoading) {
    return (
      <div className="stats-dashboard loading">
        <Loader2 size={32} className="spinning" />
        <span>Loading your DJ stats...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="stats-dashboard error">
        <p>Failed to load statistics</p>
        <button onClick={fetchStats}>Try Again</button>
      </div>
    );
  }

  if (!stats?.has_crates) {
    return (
      <div className="stats-dashboard empty">
        <Box size={48} />
        <h3>No Crates Yet</h3>
        <p>Create your first crate to start tracking your DJ patterns!</p>
        {onClose && <button onClick={onClose}>Create a Crate</button>}
      </div>
    );
  }

  const { summary, highlights, distributions } = stats;

  return (
    <div className="stats-dashboard" data-testid="statistics-dashboard">
      {/* Header */}
      <div className="stats-header">
        <div className="stats-title">
          <BarChart2 size={24} />
          <h2>Your DJ Statistics</h2>
        </div>
        {onClose && (
          <button className="close-stats-btn" onClick={onClose}>×</button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="stats-summary-grid">
        <div className="stat-card total-crates">
          <div className="stat-icon"><Box size={24} /></div>
          <div className="stat-value">{summary.total_crates}</div>
          <div className="stat-label">Crates</div>
        </div>
        <div className="stat-card total-tracks">
          <div className="stat-icon"><Music size={24} /></div>
          <div className="stat-value">{summary.total_tracks}</div>
          <div className="stat-label">Tracks</div>
        </div>
        <div className="stat-card total-time">
          <div className="stat-icon"><Clock size={24} /></div>
          <div className="stat-value">{summary.total_duration_formatted}</div>
          <div className="stat-label">Total Set Time</div>
        </div>
        <div className="stat-card collab-count">
          <div className="stat-icon"><Users size={24} /></div>
          <div className="stat-value">{summary.collab_crates}</div>
          <div className="stat-label">Collab Sets</div>
        </div>
      </div>

      {/* Crate Type Breakdown */}
      <div className="stats-breakdown">
        <div className="breakdown-item">
          <span className="breakdown-label">Mock Sets</span>
          <div className="breakdown-bar">
            <div 
              className="fill mock" 
              style={{ width: `${summary.total_crates > 0 ? (summary.mock_crates / summary.total_crates * 100) : 0}%` }}
            />
          </div>
          <span className="breakdown-value">{summary.mock_crates}</span>
        </div>
        <div className="breakdown-item">
          <span className="breakdown-label">Live Sets</span>
          <div className="breakdown-bar">
            <div 
              className="fill live" 
              style={{ width: `${summary.total_crates > 0 ? (summary.live_crates / summary.total_crates * 100) : 0}%` }}
            />
          </div>
          <span className="breakdown-value">{summary.live_crates}</span>
        </div>
      </div>

      {/* Highlights */}
      <div className="stats-highlights">
        <h3>Your Mixing Patterns</h3>
        <div className="highlights-grid">
          {highlights.most_used_key && (
            <div className="highlight-card key">
              <Key size={20} />
              <div className="highlight-content">
                <span className="highlight-value">{highlights.most_used_key}</span>
                <span className="highlight-label">Favorite Key ({highlights.key_count} tracks)</span>
              </div>
            </div>
          )}
          {highlights.avg_bpm && (
            <div className="highlight-card bpm">
              <TrendingUp size={20} />
              <div className="highlight-content">
                <span className="highlight-value">{highlights.avg_bpm} BPM</span>
                <span className="highlight-label">
                  Average ({highlights.bpm_range?.min}-{highlights.bpm_range?.max} range)
                </span>
              </div>
            </div>
          )}
          {highlights.avg_energy && (
            <div className="highlight-card energy">
              <Zap size={20} />
              <div className="highlight-content">
                <span className="highlight-value">{highlights.avg_energy}/10</span>
                <span className="highlight-label">Average Energy</span>
              </div>
            </div>
          )}
          {highlights.top_genre && (
            <div className="highlight-card genre">
              <Award size={20} />
              <div className="highlight-content">
                <span className="highlight-value">{highlights.top_genre}</span>
                <span className="highlight-label">Top Genre</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Key Distribution */}
      {Object.keys(distributions.keys).length > 0 && (
        <div className="stats-distribution keys">
          <h3>Key Distribution</h3>
          <div className="distribution-bars">
            {Object.entries(distributions.keys).slice(0, 8).map(([key, count]) => {
              const maxCount = Math.max(...Object.values(distributions.keys));
              return (
                <div key={key} className="dist-bar-item">
                  <span className="dist-label">{key}</span>
                  <div className="dist-bar-container">
                    <div 
                      className="dist-bar"
                      style={{ width: `${(count / maxCount) * 100}%` }}
                    />
                  </div>
                  <span className="dist-count">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Genre Distribution */}
      {Object.keys(distributions.genres).length > 0 && (
        <div className="stats-distribution genres">
          <h3>Genre Breakdown</h3>
          <div className="genre-pills">
            {Object.entries(distributions.genres).map(([genre, count]) => (
              <span key={genre} className="genre-pill">
                {genre} <span className="count">{count}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StatisticsDashboard;
